import React, { useEffect, useRef, useState } from 'react';
import { MoreVertical, Pencil, RotateCcw, XCircle } from 'lucide-react';
import '../../Components/ComponentActionsDropdown.css';

interface EntryActionsMenuProps {
    active: boolean;
    disabled?: boolean;
    onEdit: () => void;
    onClose: () => void;
    onReopen: () => void;
}

/** Three-dot menu in the köplats detail header: Redigera, and Avsluta or Återöppna. Same look as the component pages' actions menu. */
const EntryActionsMenu: React.FC<EntryActionsMenuProps> = ({ active, disabled = false, onEdit, onClose, onReopen }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const outside = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
        document.addEventListener('mousedown', outside);
        document.addEventListener('keydown', escape, true);
        return () => {
            document.removeEventListener('mousedown', outside);
            document.removeEventListener('keydown', escape, true);
        };
    }, [open]);

    const pick = (action: () => void) => () => { setOpen(false); action(); };

    return (
        <div ref={ref} className="actions-dropdown">
            <button type="button" className={`actions-dropdown-button ${open ? 'actions-dropdown-button--active' : ''}`}
                onClick={() => setOpen(o => !o)} title="Åtgärder" aria-label="Åtgärder" aria-haspopup="menu" aria-expanded={open}>
                <MoreVertical size={18} color="#666" />
            </button>
            {open && (
                <div className="actions-dropdown-menu" role="menu">
                    <button type="button" role="menuitem" className="actions-dropdown-item" onClick={pick(onEdit)}>
                        <Pencil size={18} color="#6c757d" /><span>Redigera</span>
                    </button>
                    {active ? (
                        <button type="button" role="menuitem" className="actions-dropdown-item text-danger" onClick={pick(onClose)}>
                            <XCircle size={18} /><span>Avsluta köplats</span>
                        </button>
                    ) : (
                        <button type="button" role="menuitem" className="actions-dropdown-item" disabled={disabled} onClick={pick(onReopen)}>
                            <RotateCcw size={18} color="#6c757d" /><span>Återöppna köplats</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default EntryActionsMenu;
